import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getSourceOptions } from '../../utils/filterUtils';
import { getSources } from '../services/api';
import { ArrowDownSLineIconV7 } from '../../components/ui/figma-icons-v7';
import { CandidateSourceAvailability, normalizeSourceId, resolveAvailableSource } from '../utils/sourceAvailability';

interface SourceTabsProps {
  activeSource: string;
  onSourceChange: (source: string) => void;
  className?: string;
  dynamic?: boolean;
  sources?: CandidateSourceAvailability[] | null;
}

const SourceTabs: React.FC<SourceTabsProps> = ({
  activeSource,
  onSourceChange,
  className = '',
  dynamic = false,
  sources,
}) => {
  const [dynamicSources, setDynamicSources] = useState<CandidateSourceAvailability[] | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const onSourceChangeRef = useRef(onSourceChange);

  useEffect(() => {
    onSourceChangeRef.current = onSourceChange;
  }, [onSourceChange]);

  useEffect(() => {
    if (sources !== undefined) return;
    if (!dynamic) return;
    let cancelled = false;
    getSources()
      .then((data: { sources?: CandidateSourceAvailability[] }) => {
        if (!cancelled) setDynamicSources(Array.isArray(data.sources) ? data.sources : []);
      })
      .catch(() => {
        if (!cancelled) {
          setDynamicSources([]);
        }
      });
    return () => { cancelled = true; };
  }, [dynamic, sources]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const staticOptions = useMemo(() => getSourceOptions(), []);
  const sourceList = sources !== undefined ? sources : dynamicSources;
  const normalizedActiveSource = normalizeSourceId(activeSource);

  const tabs = dynamic
    ? (sourceList || [])
        .reduce<Array<{ value: string; label: string }>>((acc, source) => {
          const value = normalizeSourceId(source.id);
          if (!value || source.available !== true) return acc;
          acc.push({
            value,
            label: source.name || source.id || value,
          });
          return acc;
        }, [])
    : staticOptions.map(opt => ({
        value: opt.value,
        label: opt.label,
      }));

  useEffect(() => {
    if (!sourceList) return;
    const resolvedSource = resolveAvailableSource(activeSource, sourceList);
    if (resolvedSource && resolvedSource !== normalizedActiveSource) {
      onSourceChangeRef.current(resolvedSource);
    }
  }, [activeSource, normalizedActiveSource, sourceList]);

  const activeTab =
    tabs.find(t => t.value === normalizedActiveSource)
    || (!dynamic ? staticOptions.find(opt => opt.value === normalizedActiveSource) : undefined)
    || (!dynamic ? tabs[0] : undefined);

  const buttonLabel = activeTab?.label || (dynamic && sourceList ? 'Источник' : 'Загрузка...');

  const handleSelect = (value: string) => {
    if (value !== normalizedActiveSource) {
      onSourceChange(value);
    }
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={tabs.length === 0}
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center gap-2 bg-[#F3F4F6] rounded-xl px-3 py-2.5 cursor-pointer transition-colors hover:bg-[#EAECF0] disabled:cursor-default disabled:opacity-60"
      >
        <span className="text-sm font-medium text-[#0A0D14] leading-5 whitespace-nowrap tracking-[-0.006em]">
          {tabs.length === 0 && dynamic && sourceList ? 'Нет источников' : buttonLabel}
        </span>
        <ArrowDownSLineIconV7
          size={20}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && tabs.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-[60] min-w-full overflow-hidden">
          {tabs.map(tab => (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleSelect(tab.value)}
              className={`
                w-full text-left px-3 py-2 text-sm font-medium transition-colors cursor-pointer rounded-xl
                ${tab.value === normalizedActiveSource
                  ? 'text-[#0A0D14] bg-[#F3F4F6]'
                  : 'text-[#525866] hover:bg-[#F9FAFB]'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SourceTabs;
